import { useState } from "react";
import { useCompanies, useUpdateCompany } from "@/hooks/useCompanies";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import type { AdminCompany } from "@/types/admin";

export function CompaniesPage() {
  useDocumentTitle("Companies");
  const { data: companies, isLoading, error } = useCompanies();
  const updateMutation = useUpdateCompany();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editCountry, setEditCountry] = useState("");

  function startEdit(company: AdminCompany) {
    setEditingId(company.id);
    setEditName(company.name);
    setEditCountry(company.country);
  }

  function handleSave(id: string) {
    updateMutation.mutate(
      { id, updates: { name: editName, country: editCountry } },
      { onSuccess: () => setEditingId(null) },
    );
  }

  function handleToggleStatus(company: AdminCompany) {
    const newStatus = company.status === "ACTIVE" ? "DISABLED" : "ACTIVE";
    updateMutation.mutate({ id: company.id, updates: { status: newStatus } });
  }

  return (
    <div>
      <h1 className="font-headline-lg text-headline-lg text-on-surface mb-stack-md">
        Companies
      </h1>

      {isLoading && (
        <p className="font-body-md text-on-surface-variant">Loading companies...</p>
      )}

      {error && (
        <p className="font-body-md text-error">Failed to load companies.</p>
      )}

      {companies && companies.length === 0 && (
        <p className="font-body-md text-on-surface-variant">No companies found.</p>
      )}

      {companies && companies.length > 0 && (
        <div className="space-y-stack-sm">
          {companies.map((c) => (
            <div
              key={c.id}
              className="bg-surface-container-lowest border border-white/5 rounded-xl p-stack-md"
            >
              {editingId === c.id ? (
                <div className="flex items-center gap-stack-sm">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 bg-surface-container-high text-on-surface font-body-md rounded-lg px-3 py-2 border border-outline-variant focus:border-primary focus:outline-none"
                  />
                  <input
                    type="text"
                    value={editCountry}
                    onChange={(e) => setEditCountry(e.target.value)}
                    placeholder="Country"
                    className="w-24 bg-surface-container-high text-on-surface font-body-md rounded-lg px-3 py-2 border border-outline-variant focus:border-primary focus:outline-none"
                  />
                  <button
                    onClick={() => handleSave(c.id)}
                    disabled={updateMutation.isPending}
                    className="px-3 py-2 bg-primary-container text-on-primary-container rounded-lg font-label-sm text-label-sm hover:opacity-90 disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-3 py-2 text-on-surface-variant font-label-sm text-label-sm hover:text-on-surface"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-body-md text-on-surface font-medium">{c.name}</p>
                    <p className="font-label-xs text-on-surface-variant">
                      {c.country} · {c.status}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(c)}
                      className="px-3 py-1 bg-surface-container-high text-on-surface rounded-lg font-label-sm text-label-sm hover:bg-surface-container-highest"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleToggleStatus(c)}
                      className={`px-3 py-1 rounded-lg font-label-sm text-label-sm ${
                        c.status === "ACTIVE"
                          ? "bg-error-container text-on-error-container"
                          : "bg-primary-container text-on-primary-container"
                      }`}
                    >
                      {c.status === "ACTIVE" ? "Disable" : "Enable"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
